import type { Policy } from '@/types';
import { planKind } from './productCases';

type PlanKind = ReturnType<typeof planKind>;

// The merchant's own coverage list arrives in `merchant_terms`, keyed by plan name
// (same keys the original PolicyDetails page read).
const TERMS_KEYS: Record<PlanKind, string[]> = {
  accidental: ['Accidental Damage'],
  extended: ['Extended Warranty', 'Manufacturing Warranty'],
  shipping: ['Shipping Warranty'],
};

// Used when the merchant hasn't sent a list. Kept product-neutral: the Figma copy names luggage parts.
const DEFAULT_COVERED: Record<PlanKind, string[]> = {
  accidental: [
    'Accidental drops and knocks that crack or dent the product',
    'Accidental damage to parts like wheels, handles, zips and locks',
    'Impact damage during normal use and travel',
  ],
  extended: [
    'Manufacturing defects after the brand warranty ends',
    'Repairs up to the invoice value in total',
    'A replacement if it can’t be repaired (the plan then ends)',
  ],
  shipping: ['Loss or damage while your order is in transit'],
};

const NOT_COVERED: Record<PlanKind, string[]> = {
  accidental: [
    'Damage during shipping or delivery — raise this with the store',
    'Normal wear and tear, scuffs and scratches that don’t affect use',
    'Loss or theft',
    'Damage from misuse or unauthorised repairs',
  ],
  extended: [
    'Accidental damage — that needs Accidental Damage Protection',
    'Cosmetic wear and tear',
    'Loss or theft',
    'Damage from misuse or unauthorised repairs',
  ],
  shipping: ['Damage after delivery', 'Normal wear and tear'],
};

/** What the plan covers: the merchant's terms when the API sends them, else the plan's standard list. */
export function coveredItems(policy: Policy): string[] {
  const kind = planKind(policy.type);
  const terms = policy.merchantTerms && typeof policy.merchantTerms === 'object' ? (policy.merchantTerms as Record<string, unknown>) : undefined;
  for (const key of TERMS_KEYS[kind]) {
    const list = terms?.[key];
    if (Array.isArray(list) && list.length) return list.map(String);
  }
  return policy.coverage?.length ? policy.coverage : DEFAULT_COVERED[kind];
}

export const notCoveredItems = (policy: Policy) => NOT_COVERED[planKind(policy.type)];

/** "eumeworld.com" from a merchant URL. */
export function siteName(url?: string) {
  if (!url) return undefined;
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}
